import React, { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js'
import { BusyIndicator } from '@/atoms/BusyIndicator'
import { Button } from '@/atoms/Button'
import { CheckBox } from '@/atoms/CheckBox'
import { paths } from '@/constants'
import { SET_FUTURE_USAGE_ON_PAYMENT_INTENT } from '@/services/NftService'
import { CollectibleProductResponse } from '@/services/NftService/types'
import { useSafeTrack } from '@/utils/analytics'
import { logger } from '@/utils/logging'

interface NewCardPaymentFormProps {
  projectId: string
  isPaymentState: boolean
  hasPaymentMethods: boolean
  paymentIntentId: string
  changeToSavedPaymentMethods: () => void
  handlePaymentMethodSelection: (paymentMethodId: string) => void
  paymentIntentData?: CollectibleProductResponse
}

const NewCardPaymentForm: React.FC<NewCardPaymentFormProps> = ({
  isPaymentState,
  hasPaymentMethods,
  changeToSavedPaymentMethods,
  paymentIntentId,
  projectId,
  paymentIntentData,
}) => {
  const stripe = useStripe()
  const elements = useElements()

  const [saveCard, setSaveCard] = useState(true)
  const [message, setMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [paymentReady, setPaymentReady] = useState(false)
  const [isPaymentInfoComplete, setIsPaymentInfoComplete] = useState(false)
  const track = useSafeTrack()

  const [setFutureUsage, { loading: setFutureUsageLoading }] = useMutation(SET_FUTURE_USAGE_ON_PAYMENT_INTENT, {
    errorPolicy: 'all',
  })

  const toggleSaveCard = () => {
    setSaveCard((prevValue) => {
      setFutureUsage({
        variables: {
          input: {
            paymentIntentId,
            setFutureUsuage: !prevValue,
          },
        },
      })

      return !prevValue
    })
  }

  const changePaymentInfoComplete = (stripeEleEvent: StripePaymentElementChangeEvent) => {
    if (!isPaymentInfoComplete && stripeEleEvent.complete) {
      setIsPaymentInfoComplete(true)
      return
    }

    if (isPaymentInfoComplete && !stripeEleEvent.complete) {
      setIsPaymentInfoComplete(false)
      return
    }
  }

  useEffect(() => {
    if (!stripe) {
      return
    }

    const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret')

    if (!clientSecret) {
      return
    }

    logger().debug('stripe', stripe)

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent?.status) {
        case 'succeeded':
          setMessage('Payment succeeded!')
          break
        case 'processing':
          setMessage('Your payment is processing.')
          break
        case 'requires_payment_method':
          setMessage('Your payment was not successful, please try again.')
          break
        default:
          setMessage('Something went wrong.')
          break
      }
    })
  }, [stripe])

  const orderId = paymentIntentData?.node?.order?.id

  console.log('orderId', { orderId, paymentIntentData })

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsLoading(true)

    track('Scene Pack Complete Purchase Clicked', {
      purchaseMethod: 'credit-card',
      projectSlug: projectId,
    })

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${paths.base}/${paths.collectibles.success(projectId)}?order=${
          paymentIntentData?.node?.order?.id
        }`,
      },
    })

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === 'card_error' || error.type === 'validation_error') {
      setMessage(error.message || 'Unknown error occurred')
    } else {
      setMessage('An unexpected error occurred.')
    }
  }

  return (
    <>
      <form id="payment-form" onSubmit={handleSubmit}>
        <PaymentElement
          id="payment-element"
          onReady={() => setPaymentReady(true)}
          onChange={changePaymentInfoComplete}
        />
        {paymentReady && (
          <div>
            <CheckBox
              checked={saveCard}
              disabled={setFutureUsageLoading}
              onClick={toggleSaveCard}
              alt="Save card for future use"
            >
              {setFutureUsageLoading ? (
                <>
                  <span className="mr-2">Updating save card preference</span>
                  <BusyIndicator width={14} showBusy={setFutureUsageLoading} />
                </>
              ) : (
                <span>Save card for future use</span>
              )}
            </CheckBox>
            <div className="flex flex-row items-center mt-4 gap-10">
              {isLoading ? (
                <div className="text-black rounded-lg border-black w-full px-6 py-3 border">Processing...</div>
              ) : (
                <Button
                  className="text-black bg-core-oxide border-none w-full py-3"
                  variant="oxide"
                  disabled={
                    !isPaymentInfoComplete ||
                    !isPaymentState ||
                    isLoading ||
                    !stripe ||
                    !elements ||
                    setFutureUsageLoading
                  }
                  id="submit"
                  type="submit"
                >
                  Complete Purchase
                </Button>
              )}
            </div>
            {hasPaymentMethods && (
              <Button
                variant="white"
                className="text-black bg-core-oxide border-none w-full mt-4"
                onClick={(e) => {
                  e.preventDefault()
                  changeToSavedPaymentMethods()
                }}
              >
                Use saved payment methods
              </Button>
            )}
          </div>
        )}
        {message && (
          <div id="payment-message" className="mt-8 w-full text-center text-red font-bold text-lg">
            {message}
          </div>
        )}
      </form>
    </>
  )
}

export default NewCardPaymentForm
